import { STORAGE_KEYS } from '@/constants/storage';
import { getInitialBrowserAuthSession } from '@/lib/mobile/auth-persistence';

import { isNonWorkspacePath } from './boot-paths';
import { BOOT_READ_TTL_MS, bootReadKey, clearBootReads, startBootRead } from './boot-read-registry';
import {
  requestFeatureGateAccess,
  requestIsPlatformAdmin,
  requestTeamMembers,
  onBootSignOut,
  requestUserOrganizations,
  sessionOf,
} from './boot-requests';
import { isQueryableOrganizationId } from './organization-id';
import { hasUnexpiredSession } from './session-expiry';

/**
 * Sends the shell's first reads as the page starts, from the stored session and the remembered
 * office. They would otherwise wait for the app bundle to evaluate, React to render and the
 * memberships to answer, in three steps one after the other. The query that owns each read takes
 * the response from the registry instead of asking again.
 *
 * Every read is pinned to the access token of the client's own session, and only when that
 * session belongs to the stored user: the answer is then that user's whoever signs in next. A read
 * for an office the person no longer belongs to is refused, answers with an error, and is asked
 * again by the query.
 */
export function startBootReads(): void {
  if (
    import.meta.env.VITE_NATIVE_SHELL === '1' ||
    isNonWorkspacePath(globalThis.location.pathname)
  ) {
    return;
  }
  // A read is an optimisation: nothing in it may stop the app from starting.
  try {
    const userId = getInitialBrowserAuthSession()?.user?.id;
    if (!userId) {
      return;
    }
    // The app's logout signal starts only once React mounts: a sign-out before then voids the reads here.
    const stopWatching = onBootSignOut(clearBootReads);
    setTimeout(stopWatching, BOOT_READ_TTL_MS);
    const clientSession = sessionOf(userId);
    const pinned =
      <Response>(send: (accessToken: string) => PromiseLike<Response>) =>
      async (): Promise<Response> => {
        const session = await clientSession;
        if (!session) {
          throw new Error('The signed-in session is not the stored one');
        }
        return await send(session.access_token);
      };

    startBootRead(
      bootReadKey.organizations(userId),
      pinned((token) => requestUserOrganizations(userId, token)),
    );
    startBootRead(
      bootReadKey.platformAdmin(userId),
      pinned((token) => requestIsPlatformAdmin(token)),
    );

    const organizationId = globalThis.localStorage.getItem(STORAGE_KEYS.SELECTED_ORGANIZATION_ID);
    if (!isQueryableOrganizationId(organizationId)) {
      return;
    }
    startBootRead(
      bootReadKey.teamMembers(userId, organizationId),
      pinned((token) => requestTeamMembers(organizationId, token)),
    );
    startBootRead(bootReadKey.featureGates(userId, organizationId), async () => {
      // A gate read with a dead token reaches Postgres as the anonymous role.
      const session = await clientSession;
      if (!hasUnexpiredSession(session)) {
        throw new Error('No live session for the gate read');
      }
      return await requestFeatureGateAccess(organizationId, session?.access_token);
    });
  } catch {
    // Storage blocked, or the session was unreadable.
  }
}
